import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import ConfirmationDialog from '../components/ConfirmationDialog';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Loading skeleton component
const ServiceCardSkeleton = () => (
  <div className="border border-light-border dark:border-border rounded-xl p-4 animate-pulse">
    <div className="flex justify-between items-start">
      <div className="space-y-2 flex-1">
        <div className="h-5 bg-slate-200 dark:bg-silver/20 rounded w-40"></div>
        <div className="h-3 bg-slate-200 dark:bg-silver/20 rounded w-24"></div>
        <div className="h-3 bg-slate-200 dark:bg-silver/20 rounded w-32"></div>
      </div>
      <div className="h-6 bg-slate-200 dark:bg-silver/20 rounded w-16"></div>
    </div>
  </div>
);

const DoctorServices = () => {
  const { user, loading: authLoading, isAuthenticated, token } = useAuth();
  const navigate = useNavigate();

  // Services state
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Doctor ID state
  const [doctorId, setDoctorId] = useState(null);

  // Form state for adding new service
  const [showAddForm, setShowAddForm] = useState(false);
  const [newService, setNewService] = useState({
    name: '',
    description: '',
    durationMinutes: 30,
    price: '',
    currency: 'GBP'
  });
  const [formError, setFormError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delete state - track which service is being deleted to prevent rapid clicks
  const [deletingServiceId, setDeletingServiceId] = useState(null);
  // Use a ref for synchronous rapid-click protection (state updates are batched/async)
  const deletingRef = useRef(null);

  // Delete confirmation dialog state
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    serviceId: null,
    serviceName: ''
  });

  // Edit state - track which service is being edited
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [editService, setEditService] = useState({
    name: '',
    description: '',
    durationMinutes: 30,
    price: '',
    currency: 'GBP'
  });
  const [editFormError, setEditFormError] = useState(null);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  // Success message state for delete feedback (kept for backwards compatibility but using toast now)

  // Initial form values for comparison
  const initialFormValues = useMemo(() => ({
    name: '',
    description: '',
    durationMinutes: 30,
    price: '',
    currency: 'GBP'
  }), []);

  // Check if form has unsaved changes (add form or edit form)
  const hasUnsavedChanges = useMemo(() => {
    // Check add form
    if (showAddForm) {
      return (
        newService.name !== initialFormValues.name ||
        newService.description !== initialFormValues.description ||
        newService.durationMinutes !== initialFormValues.durationMinutes ||
        newService.price !== initialFormValues.price ||
        newService.currency !== initialFormValues.currency
      );
    }
    // Check edit form
    if (editingServiceId !== null) {
      return true; // If editing, always consider it as having unsaved changes
    }
    return false;
  }, [showAddForm, newService, initialFormValues, editingServiceId]);

  // Use the unsaved changes hook
  const { isBlocked, confirmNavigation, cancelNavigation, checkNavigation } = useUnsavedChanges(
    hasUnsavedChanges,
    'You have unsaved changes to your service. Are you sure you want to leave without saving?'
  );

  // Handle navigation with unsaved changes check
  const handleNavigation = useCallback((path, e) => {
    if (e) e.preventDefault();
    if (!checkNavigation(path)) {
      navigate(path);
    }
  }, [checkNavigation, navigate]);

  useEffect(() => {
    // Redirect if not authenticated or not a doctor
    if (!authLoading && (!isAuthenticated || user?.role !== 'doctor')) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Fetch doctor ID from /api/auth/me endpoint
  useEffect(() => {
    const fetchDoctorId = async () => {
      if (!isAuthenticated || user?.role !== 'doctor') return;

      try {
        const response = await fetch(`${API_URL}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const userData = await response.json();
          if (userData.doctorId) {
            setDoctorId(userData.doctorId);
          }
        }
      } catch (err) {
        console.error('Error fetching doctor ID:', err);
      }
    };

    fetchDoctorId();
  }, [isAuthenticated, user, token]);

  // Fetch services
  const fetchServices = useCallback(async () => {
    if (!doctorId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/doctors/${doctorId}/services`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch services');
      }

      const data = await response.json();
      setServices(data.services || []);
    } catch (err) {
      console.error('Error fetching services:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [doctorId, token]);

  useEffect(() => {
    if (doctorId) {
      fetchServices();
    }
  }, [doctorId, fetchServices]);

  // Handle form submission
  const handleAddService = async (e) => {
    e.preventDefault();
    setFormError(null);
    setIsSubmitting(true);

    // Validate form
    if (!newService.name.trim()) {
      setFormError('Service name is required');
      setIsSubmitting(false);
      return;
    }
    if (!newService.price || parseFloat(newService.price) <= 0) {
      setFormError('Price must be greater than 0');
      setIsSubmitting(false);
      return;
    }
    const durationValue = parseInt(newService.durationMinutes);
    if (!newService.durationMinutes || durationValue <= 0) {
      setFormError('Duration must be greater than 0');
      setIsSubmitting(false);
      return;
    }
    if (durationValue < 5) {
      setFormError('Duration must be at least 5 minutes');
      setIsSubmitting(false);
      return;
    }
    if (durationValue > 480) {
      setFormError('Duration cannot exceed 480 minutes (8 hours)');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/doctors/${doctorId}/services`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newService.name.trim(),
          description: newService.description.trim(),
          durationMinutes: parseInt(newService.durationMinutes),
          price: parseFloat(newService.price),
          currency: newService.currency
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create service');
      }

      // Reset form and refresh services
      const createdServiceName = newService.name.trim();
      setNewService({
        name: '',
        description: '',
        durationMinutes: 30,
        price: '',
        currency: 'GBP'
      });
      setShowAddForm(false);
      fetchServices();

      // Show success toast
      toast.success(`Service "${createdServiceName}" created successfully!`);
    } catch (err) {
      console.error('Error creating service:', err);
      setFormError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open delete confirmation dialog
  const handleDeleteClick = (service) => {
    setDeleteConfirmation({
      isOpen: true,
      serviceId: service.id,
      serviceName: service.name
    });
  };

  // Cancel delete confirmation
  const handleDeleteCancel = () => {
    setDeleteConfirmation({
      isOpen: false,
      serviceId: null,
      serviceName: ''
    });
  };

  // Handle service deletion with rapid-click protection (called after confirmation)
  const handleDeleteConfirm = async () => {
    const serviceId = deleteConfirmation.serviceId;
    const serviceName = deleteConfirmation.serviceName;

    // Rapid-click protection using ref for SYNCHRONOUS check (state updates are batched/async)
    if (deletingRef.current !== null) {
      console.log(`[DELETE] Ignoring rapid click - deletion already in progress for ${deletingRef.current}`);
      return;
    }

    // Set the ref IMMEDIATELY (synchronous) to block any further clicks
    deletingRef.current = serviceId;
    // Also set state for UI updates (shows loading spinner)
    setDeletingServiceId(serviceId);

    try {
      const response = await fetch(`${API_URL}/doctors/${doctorId}/services/${serviceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete service');
      }

      // Remove the service from local state
      setServices(prev => prev.filter(s => s.id !== serviceId));
      console.log(`[DELETE] Service ${serviceId} deleted successfully`);

      // Close the confirmation dialog
      setDeleteConfirmation({
        isOpen: false,
        serviceId: null,
        serviceName: ''
      });

      // Show success toast
      toast.success(`"${serviceName}" has been deleted successfully.`);
    } catch (err) {
      console.error('Error deleting service:', err);
      setError(err.message);
      // Close dialog on error too
      setDeleteConfirmation({
        isOpen: false,
        serviceId: null,
        serviceName: ''
      });
    } finally {
      // Clear both ref and state
      deletingRef.current = null;
      setDeletingServiceId(null);
    }
  };

  // Start editing a service
  const handleEditClick = (service) => {
    setEditingServiceId(service.id);
    setEditService({
      name: service.name,
      description: service.description || '',
      durationMinutes: service.durationMinutes,
      price: service.price.toString(),
      currency: service.currency
    });
    setEditFormError(null);
  };

  // Cancel editing
  const handleEditCancel = () => {
    setEditingServiceId(null);
    setEditService({
      name: '',
      description: '',
      durationMinutes: 30,
      price: '',
      currency: 'GBP'
    });
    setEditFormError(null);
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditFormError(null);
    setIsEditSubmitting(true);

    // Validate form
    if (!editService.name.trim()) {
      setEditFormError('Service name is required');
      setIsEditSubmitting(false);
      return;
    }
    if (!editService.price || parseFloat(editService.price) <= 0) {
      setEditFormError('Price must be greater than 0');
      setIsEditSubmitting(false);
      return;
    }
    const durationValue = parseInt(editService.durationMinutes);
    if (!editService.durationMinutes || durationValue <= 0) {
      setEditFormError('Duration must be greater than 0');
      setIsEditSubmitting(false);
      return;
    }
    if (durationValue < 5) {
      setEditFormError('Duration must be at least 5 minutes');
      setIsEditSubmitting(false);
      return;
    }
    if (durationValue > 480) {
      setEditFormError('Duration cannot exceed 480 minutes (8 hours)');
      setIsEditSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/doctors/${doctorId}/services/${editingServiceId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editService.name.trim(),
          description: editService.description.trim(),
          durationMinutes: parseInt(editService.durationMinutes),
          price: parseFloat(editService.price),
          currency: editService.currency
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update service');
      }

      const data = await response.json();

      // Update the service in local state
      setServices(prev => prev.map(s =>
        s.id === editingServiceId ? data.service : s
      ));

      // Clear edit state
      const updatedServiceName = editService.name.trim();
      setEditingServiceId(null);
      setEditService({
        name: '',
        description: '',
        durationMinutes: 30,
        price: '',
        currency: 'GBP'
      });

      // Show success toast
      toast.success(`Service "${updatedServiceName}" updated successfully!`);
    } catch (err) {
      console.error('Error updating service:', err);
      setEditFormError(err.message);
    } finally {
      setIsEditSubmitting(false);
    }
  };

  // Show loading during auth check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-light-text dark:border-pure-white mx-auto mb-4"></div>
          <p className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'doctor') {
    return null;
  }

  return (
    <div className="min-h-[calc(100vh-5rem)] px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <button
              onClick={(e) => handleNavigation('/doctor/dashboard', e)}
              className="flex items-center gap-2 text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-silver mb-4 transition-smooth"
              data-testid="back-to-dashboard-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Dashboard
            </button>
            <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
              Manage Services
            </h1>
            <p className="font-body text-light-text-secondary dark:text-silver/60">
              Add and manage the services you offer to patients
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="btn-primary"
            data-testid="add-service-button"
          >
            + Add Service
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Success messages now use toast notifications */}

        {/* Add Service Form */}
        {showAddForm && (
          <div className="mb-8 glass border border-light-border dark:border-border rounded-2xl p-6">
            <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
              Add New Service
            </h2>

            {formError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-sm">{formError}</p>
              </div>
            )}

            <form onSubmit={handleAddService} className="space-y-4">
              <div>
                <label htmlFor="service-name" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                  Service Name *
                </label>
                <input
                  type="text"
                  id="service-name"
                  value={newService.name}
                  onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                  placeholder="e.g., General Consultation"
                  className="input-field"
                  data-testid="service-name-input"
                />
              </div>

              <div>
                <label htmlFor="service-description" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                  Description
                </label>
                <textarea
                  id="service-description"
                  value={newService.description}
                  onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                  placeholder="Brief description of the service..."
                  className="input-field min-h-[80px] resize-none"
                  rows={3}
                  data-testid="service-description-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="service-duration" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                    Duration (minutes) *
                  </label>
                  <input
                    type="number"
                    id="service-duration"
                    value={newService.durationMinutes}
                    onChange={(e) => setNewService({ ...newService, durationMinutes: e.target.value })}
                    placeholder="30"
                    className="input-field"
                    data-testid="service-duration-input"
                  />
                  <p className="font-mono text-xs text-slate-400 dark:text-silver/40 mt-1">
                    Enter duration between 5 and 480 minutes
                  </p>
                </div>

                <div>
                  <label htmlFor="service-price" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                    Price *
                  </label>
                  <input
                    type="number"
                    id="service-price"
                    step="0.01"
                    value={newService.price}
                    onChange={(e) => setNewService({ ...newService, price: e.target.value })}
                    placeholder="50.00"
                    className="input-field"
                    data-testid="service-price-input"
                  />
                </div>

                <div>
                  <label htmlFor="service-currency" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                    Currency
                  </label>
                  <select
                    id="service-currency"
                    value={newService.currency}
                    onChange={(e) => setNewService({ ...newService, currency: e.target.value })}
                    className="input-field"
                    data-testid="service-currency-select"
                  >
                    <option value="GBP">GBP (£)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setFormError(null);
                    setNewService({
                      name: '',
                      description: '',
                      durationMinutes: 30,
                      price: '',
                      currency: 'GBP'
                    });
                  }}
                  className="flex-1 py-3 px-6 border border-light-border dark:border-border rounded-xl text-light-text dark:text-silver hover:bg-slate-100 dark:hover:bg-silver/5 transition-smooth"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="save-service-button"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    'Save Service'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Services List */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
            Your Services
          </h2>

          {isLoading ? (
            <div className="space-y-4">
              <ServiceCardSkeleton />
              <ServiceCardSkeleton />
              <ServiceCardSkeleton />
            </div>
          ) : services.length > 0 ? (
            <div className="space-y-4" data-testid="services-list">
              {services.map((service) => (
                <div
                  key={service.id}
                  className="border border-light-border dark:border-border rounded-xl p-4 hover:border-slate-400 dark:hover:border-white/20 transition-smooth"
                  data-testid={`service-item-${service.id}`}
                >
                  {editingServiceId === service.id ? (
                    /* Edit Form */
                    <form onSubmit={handleEditSubmit} className="space-y-4">
                      {editFormError && (
                        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                          <p className="text-red-400 text-sm">{editFormError}</p>
                        </div>
                      )}
                      <div>
                        <label className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                          Service Name *
                        </label>
                        <input
                          type="text"
                          value={editService.name}
                          onChange={(e) => setEditService({ ...editService, name: e.target.value })}
                          placeholder="e.g., General Consultation"
                          className="input-field"
                          data-testid="edit-service-name-input"
                        />
                      </div>
                      <div>
                        <label className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                          Description
                        </label>
                        <textarea
                          value={editService.description}
                          onChange={(e) => setEditService({ ...editService, description: e.target.value })}
                          placeholder="Brief description of the service..."
                          className="input-field min-h-[60px] resize-none"
                          rows={2}
                          data-testid="edit-service-description-input"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                            Duration (minutes) *
                          </label>
                          <input
                            type="number"
                            value={editService.durationMinutes}
                            onChange={(e) => setEditService({ ...editService, durationMinutes: e.target.value })}
                            placeholder="30"
                            className="input-field"
                            data-testid="edit-service-duration-input"
                          />
                        </div>
                        <div>
                          <label className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                            Price *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={editService.price}
                            onChange={(e) => setEditService({ ...editService, price: e.target.value })}
                            placeholder="50.00"
                            className="input-field"
                            data-testid="edit-service-price-input"
                          />
                        </div>
                        <div>
                          <label className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                            Currency
                          </label>
                          <select
                            value={editService.currency}
                            onChange={(e) => setEditService({ ...editService, currency: e.target.value })}
                            className="input-field"
                            data-testid="edit-service-currency-select"
                          >
                            <option value="GBP">GBP (£)</option>
                            <option value="USD">USD ($)</option>
                            <option value="EUR">EUR (€)</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-3 pt-2">
                        <button
                          type="button"
                          onClick={handleEditCancel}
                          className="flex-1 py-2 px-4 border border-light-border dark:border-border rounded-lg text-light-text dark:text-silver hover:bg-slate-100 dark:hover:bg-silver/5 transition-smooth"
                          data-testid="cancel-edit-button"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isEditSubmitting}
                          className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="save-edit-button"
                        >
                          {isEditSubmitting ? (
                            <span className="flex items-center justify-center gap-2">
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Saving...
                            </span>
                          ) : (
                            'Save Changes'
                          )}
                        </button>
                      </div>
                    </form>
                  ) : (
                    /* Normal Display */
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-body font-medium text-light-text dark:text-pure-white">
                          {service.name}
                        </h3>
                        {service.description && (
                          <p className="font-body text-sm text-light-text-secondary dark:text-silver/60 mt-1">
                            {service.description}
                          </p>
                        )}
                        <p className="font-mono text-xs text-slate-400 dark:text-silver/40 mt-2">
                          {service.durationMinutes} minutes
                          {!service.isActive && (
                            <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs">
                              Inactive
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="text-right flex flex-col items-end gap-2">
                        <p className="font-display text-xl italic text-light-text dark:text-pure-white">
                          {service.currency === 'GBP' ? '£' : service.currency === 'EUR' ? '€' : '$'}
                          {service.price.toFixed(2)}
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditClick(service)}
                            disabled={editingServiceId !== null || deletingServiceId !== null}
                            className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-lg border transition-smooth border-blue-500/30 text-blue-400 hover:bg-blue-500/10 hover:border-blue-500/50 ${
                              (editingServiceId !== null || deletingServiceId !== null) ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            data-testid={`edit-service-${service.id}`}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(service)}
                            disabled={deletingServiceId !== null || editingServiceId !== null}
                            className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-lg border transition-smooth ${
                              deletingServiceId === service.id
                                ? 'border-red-500/50 text-red-400 bg-red-500/10 cursor-not-allowed'
                                : 'border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50'
                            } ${(deletingServiceId !== null && deletingServiceId !== service.id) || editingServiceId !== null ? 'opacity-50 cursor-not-allowed' : ''}`}
                            data-testid={`delete-service-${service.id}`}
                          >
                            {deletingServiceId === service.id ? (
                              <span className="flex items-center gap-1">
                                <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Deleting...
                              </span>
                            ) : 'Delete'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12" data-testid="no-services-message">
              <svg className="w-16 h-16 text-slate-300 dark:text-silver/20 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <p className="font-body text-light-text-secondary dark:text-silver/60 mb-2">
                No services added yet
              </p>
              <p className="font-body text-sm text-slate-400 dark:text-silver/40 mb-6">
                Add your first service to start receiving bookings
              </p>
              <button
                onClick={() => setShowAddForm(true)}
                className="btn-primary"
              >
                + Add Your First Service
              </button>
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-8 p-6 bg-blue-500/10 border border-blue-500/30 rounded-2xl">
          <h3 className="font-mono text-sm uppercase tracking-widest text-blue-400 mb-2">
            Pro Tip
          </h3>
          <p className="font-body text-sm text-slate-600 dark:text-silver/80">
            Services you add here will appear on your public profile in the marketplace.
            Patients can select these services when booking appointments with you.
            Make sure to set competitive prices and clear descriptions to attract more bookings.
          </p>
        </div>
      </div>

      {/* Unsaved Changes Warning Dialog */}
      <UnsavedChangesDialog
        isOpen={isBlocked}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
        message="You have unsaved changes to your service. Are you sure you want to leave without saving?"
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteConfirmation.isOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="Delete Service"
        message={`Are you sure you want to delete "${deleteConfirmation.serviceName}"? This action cannot be undone and any upcoming appointments for this service may be affected.`}
        confirmText="Delete Service"
        cancelText="Cancel"
        isLoading={deletingServiceId === deleteConfirmation.serviceId}
        variant="danger"
      />
    </div>
  );
};

export default DoctorServices;
